import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Button,
  ButtonLink,
  Column,
  ContentBox,
  ContentPage,
  DescriptionList,
  Notice,
  PageActions,
  Row,
} from "@ficatranquilo/contabilidade.com-ui";
import { schedulePeriod } from "@helpers/competencePeriod";
import { fixedAdjustmentCompetence, isCompetenceOpen } from "@helpers/fixedAdjustmentCompetence";
import { hasPermission } from "@helpers/permissions";
import { canOpenScreen } from "@helpers/companyScreens";
import { downloadTaxInvoice } from "@helpers/taxInvoiceFile";
import { scheduleHasValue } from "@helpers";
import useService from "@hooks/useService";
import CompetenceValueBlock from "@components/CompetenceValueBlock";
import MarkPaidModal from "@components/MarkPaidModal";
import ApproveInvoiceModal from "@components/ApproveInvoiceModal";
import RejectInvoiceModal from "@components/RejectInvoiceModal";
import EditScheduleModal from "@components/EditScheduleModal";
import DeleteScheduleModal from "@components/DeleteScheduleModal";
import { Money, escapeHtml, formatCompetencia, usePageHeader } from "@components/ow";
import { remunerationSummary } from "../../Common/ContractDetail";
import {
  Inline,
  InvoiceFile,
  PaymentDetailSkeleton,
  Stack,
  TwoLines,
  competenceOf,
  contractValueItems,
  historyItems,
  paymentItems,
} from "../../Common/PaymentSchedules";

// Detalhe de um cronograma de pagamento (modelo Detalhe do DS OneWork, sem abas):
// ações da página no PageActions, avisos no topo e os blocos em duas colunas.
// As ações são só do ADM; "Ver cronogramas" lê.

const LIST_HREF = "/c/payment-schedules";

export default function PaymentScheduleDetails() {
  const params = useParams();
  const navigate = useNavigate();
  const paymentScheduleService = useService("payment-schedules");
  const taxInvoicesService = useService("taxInvoices");
  const user = JSON.parse(localStorage.getItem("user_ow") || "{}");
  // "Ver cronogramas" só lê: ações só do ADM; baixar a NF fica para "Ver NFs"
  const isAdmin = user?.role === "ADM";
  const canDownloadInvoice = hasPermission("tax_invoices:read");
  const canOpenContract = canOpenScreen("contracts", user);

  const [loading, setLoading] = useState(true);
  const [schedule, setSchedule] = useState(null);
  const [recalculating, setRecalculating] = useState(false);
  const [modal, setModal] = useState(null); // "pay" | "approve" | "reject" | "edit" | "delete"

  // A API responde com "contract" (minúsculo); "Contract" fica como fallback
  // para respostas antigas/cacheadas.
  const contract = schedule?.contract || schedule?.Contract;
  const isHourly = contract?.remuneration_type === "HOURLY";
  const nf = schedule?.taxInvoice;
  const pending = schedule?.status === "PENDING";
  const name = schedule?.user?.name || contract?.User?.name || "Prestador";

  // O Layout escapa o título; o breadcrumb é texto
  usePageHeader({
    title: schedule ? `${name} · ${formatCompetencia(competenceOf(schedule), true)}` : "Pagamento",
    breadcrumb: [
      { page: "Pagamentos", route: LIST_HREF },
      { page: schedule ? name : "Pagamento", route: `${LIST_HREF}/${params.id}` },
    ],
  });

  useEffect(() => {
    fetchSchedule();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  async function fetchSchedule() {
    try {
      const result = await paymentScheduleService.get(params.id);
      if (result && result.data) {
        setSchedule(result.data);
        setLoading(false);
        return;
      }
      throw new Error("Cronograma não encontrado");
    } catch (error) {
      console.error(error);
      const errorMessage = error?.response?.data?.msg || error?.message || "Erro ao carregar detalhes do cronograma";
      // toastId: o StrictMode do dev busca duas vezes
      toast.error(errorMessage, { toastId: "payment-schedule-load" });
      navigate(LIST_HREF);
    }
  }

  function closeModal() {
    setModal(null);
  }

  function reload() {
    setModal(null);
    fetchSchedule();
  }

  function openMarkPaid() {
    // Sem valor definido não há o que dar baixa: o back recusa
    if (!scheduleHasValue(schedule)) {
      toast.error("Este cronograma ainda não tem valor definido. Defina o valor antes de registrar o pagamento.");
      return;
    }
    setModal("pay");
  }

  async function handleRecalculateHourly() {
    setRecalculating(true);
    try {
      const result = await paymentScheduleService.recalculateHourly(params.id);
      if (result && !result.error) {
        toast.success(result.msg || "Valor recalculado com sucesso!");
        fetchSchedule();
      } else {
        throw new Error(result?.msg || "Erro ao recalcular");
      }
    } catch (error) {
      const errorMessage = error?.response?.data?.msg || error?.message || "Erro ao recalcular horas";
      toast.error(errorMessage);
    }
    setRecalculating(false);
  }

  if (loading || !schedule) return <PaymentDetailSkeleton />;

  const period = schedulePeriod(schedule);
  const competence = fixedAdjustmentCompetence(schedule, period);
  const open = isCompetenceOpen(schedule);
  const canRecalculate = isAdmin && isHourly && pending && !schedule.tax_invoice_id;
  const canEditOrDelete = isAdmin && pending && !schedule.tax_invoice_id;
  const canMarkPaid = isAdmin && pending && nf?.status === "APPROVED";
  const canDecideInvoice = isAdmin && nf?.status === "PENDING";
  // Nota aprovada ainda pode ser recusada enquanto o pagamento está em aberto
  const canRejectApproved = isAdmin && pending && nf?.status === "APPROVED";
  const contractItems = contract
    ? [
        {
          label: "Prestador",
          value: <TwoLines main={escapeHtml(name)} sub={schedule.user?.company_name ? escapeHtml(schedule.user.company_name) : null} />,
        },
        { label: "Remuneração", value: remunerationSummary(contract) },
        ...contractValueItems(contract),
      ]
    : [];

  function invoiceBlock() {
    if (!nf) {
      // Competência encerrada não recebe nota: não há o que cobrar
      if (!pending) return null;
      return <DescriptionList $title="Nota fiscal" $columns={1} $items={[{ label: "Arquivo", value: "Sem nota" }]} />;
    }
    return (
      <Stack $gap="12px">
        <DescriptionList
          $title="Nota fiscal"
          $items={[
            {
              label: "Arquivo",
              span: 2,
              value: <InvoiceFile invoice={nf} onDownload={canDownloadInvoice ? () => downloadTaxInvoice(taxInvoicesService, nf) : null} />,
            },
            { label: "Valor da nota", value: nf.value ? <Money value={nf.value} /> : null },
          ]}
        />
        {nf.status === "REJECTED" && nf.rejection_reason ? (
          <Notice $variant="red" $title="Motivo da recusa" $text="%s" $replaces={[escapeHtml(nf.rejection_reason)]} />
        ) : null}
        {canDecideInvoice ? (
          <Inline>
            <Button $text="Aprovar nota" $variant="outline" $size="small" $noWidth $action={() => setModal("approve")} />
            <Button $text="Recusar nota" $variant="outline_danger" $size="small" $noWidth $action={() => setModal("reject")} />
          </Inline>
        ) : null}
        {canRejectApproved ? (
          <Inline>
            <Button $text="Recusar nota" $variant="outline_danger" $size="small" $noWidth $action={() => setModal("reject")} />
          </Inline>
        ) : null}
      </Stack>
    );
  }

  const hasPageActions = canRecalculate || canEditOrDelete || canMarkPaid;

  return (
    <ContentPage>
      {hasPageActions ? (
        <PageActions>
          {canRecalculate ? (
            <Button $text="Recalcular horas" $variant="outline" $isLoading={recalculating} $action={handleRecalculateHourly} />
          ) : null}
          {canEditOrDelete ? (
            <>
              <Button $text="Editar" $variant="outline" $action={() => setModal("edit")} />
              <Button $text="Excluir" $variant="outline_danger" $action={() => setModal("delete")} />
            </>
          ) : null}
          {canMarkPaid ? <Button $text="Marcar como pago" $variant="primary" $action={openMarkPaid} /> : null}
        </PageActions>
      ) : null}

      <ContentBox>
        <Stack $gap="24px">
          {open ? (
            <Notice
              $variant="orange"
              $title="Apuração parcial"
              $text="O valor desta competência ainda muda a cada aprovação de horas, até a nota ser enviada ou o prazo dela passar."
            />
          ) : null}
          {competence?.isFrozen ? (
            <Notice
              $variant="gray"
              $title="Encerrada para lançamento"
              $text="O prazo da nota passou e o valor não é mais recalculado. O saldo de horas é acertado na competência seguinte."
            />
          ) : null}
          {isAdmin && isHourly && pending && schedule.tax_invoice_id ? (
            <Notice $variant="gray" $text="Para recalcular as horas, recuse a nota fiscal vinculada." />
          ) : null}

          <CompetenceValueBlock schedule={schedule} period={period} />

          <Row $gap={20} $alignItems="flex-start">
            <Column $columns={6}>
              <DescriptionList $title="Pagamento" $items={paymentItems(schedule, period)} />
            </Column>
            <Column $columns={6}>
              <Stack $gap="20px">
                {invoiceBlock()}
                {contract ? (
                  <Stack $gap="12px">
                    <DescriptionList $title="Contrato" $items={contractItems} />
                    {canOpenContract ? (
                      // Num flex em coluna o link esticaria até a largura toda
                      <div>
                        <ButtonLink
                          $text="Ver contrato"
                          $action={() => navigate(`/c/contracts/${schedule.contract_id}`)}
                          $noPadding
                          $noWidth
                          $hasIcon
                        />
                      </div>
                    ) : null}
                  </Stack>
                ) : null}
                <DescriptionList $title="Histórico" $items={historyItems(schedule)} />
              </Stack>
            </Column>
          </Row>
        </Stack>
      </ContentBox>

      {modal === "pay" ? <MarkPaidModal schedule={schedule} onClose={closeModal} onPaid={reload} /> : null}
      {modal === "approve" && nf ? <ApproveInvoiceModal invoice={nf} schedule={schedule} onClose={closeModal} onApproved={reload} /> : null}
      {modal === "reject" && nf ? <RejectInvoiceModal invoice={nf} schedule={schedule} onClose={closeModal} onRejected={reload} /> : null}
      {modal === "edit" ? <EditScheduleModal schedule={schedule} isHourly={isHourly} onClose={closeModal} onSaved={reload} /> : null}
      {modal === "delete" ? (
        <DeleteScheduleModal
          schedule={schedule}
          onClose={closeModal}
          onDeleted={() => {
            setModal(null);
            navigate(LIST_HREF);
          }}
        />
      ) : null}
    </ContentPage>
  );
}
